import { CalendarX, ClipboardList, TriangleAlert } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { errorText } from '../../components/ErrorToast';
import { Button, Card, EmptyState } from '../../components/ui';
import { ApiError } from '../../lib/errors';
import { SETTINGS_PATH } from '../../lib/nav';
import { useRole } from '../../lib/role';
import { LABEL, T, say } from './texts';

/**
 * Ошибка загрузки (contracts/ui.md, «Состояния»; дизайн-система, EmptyState): что случилось, «Код ошибки: …» и
 * «Повторить». Адреса правил нет (табель не на движке, `404`) — «правила ещё не настроены».
 * 007, поправка 20.10.2026 (FR-004b): правил нет по понятной причине — это не ошибка, кода ошибки и «Повторить» нет:
 * площадка до запуска (`RULES_NOT_PREPARED`) — где их готовят, администратору системы — «Открыть подключение
 * площадки»; дата раньше первой версии (`RULES_NOT_ON_DATE`) — с какой даты действуют правила.
 */
export function LoadError({
  error,
  title,
  onRetry,
  objectId,
  onToday,
}: {
  error: unknown;
  title: string;
  onRetry: () => void;
  /** Объект страницы: площадка для «Открыть подключение площадки». */
  objectId?: string;
  /** «Открыть действующие правила» — правила на сегодня. */
  onToday?: () => void;
}) {
  const e = error instanceof ApiError ? error : null;
  if (e?.code === 'RULES_NOT_PREPARED') return <NotPrepared objectId={objectId} />;
  if (e?.code === 'RULES_NOT_ON_DATE') {
    return (
      <Card>
        <EmptyState
          icon={CalendarX}
          title={LABEL.notOnDateTitle}
          description={`${e.message}.`}
          action={onToday && <Button onClick={onToday}>{LABEL.openCurrent}</Button>}
        />
      </Card>
    );
  }
  const description = e?.code === 'NOT_FOUND' ? T.notConfigured : `${errorText(error, title)}.`;
  return (
    <Card>
      <EmptyState
        icon={TriangleAlert}
        title={title}
        description={description}
        action={
          <div className="flex flex-col items-center gap-3">
            {e?.requestId && (
              <p className="text-[12px] text-ink-3 select-all">{say.errorCode(e.requestId)}</p>
            )}
            <Button onClick={onRetry}>{LABEL.retry}</Button>
          </div>
        }
      />
    </Card>
  );
}

/**
 * Площадка до запуска без правил к запуску (FR-004b; дизайн-система EmptyState «ещё нет»): заголовок, одна фраза — где и
 * кто готовит правила, кнопка-акцент — только администратору системы площадки.
 */
function NotPrepared({ objectId }: { objectId: string | undefined }) {
  const { role } = useRole();
  const navigate = useNavigate();
  const sysadmin = role === 'sysadmin' && objectId !== undefined;
  return (
    <Card>
      <EmptyState
        icon={ClipboardList}
        title={LABEL.notPreparedTitle}
        description={T.notPrepared}
        action={
          sysadmin && (
            <Button variant="primary" onClick={() => navigate(SETTINGS_PATH.site(objectId))}>
              {LABEL.openSite}
            </Button>
          )
        }
      />
    </Card>
  );
}

/** Сбой связи или сервера — в серверный журнал (принцип II); отказ по правам и «нет адреса» — нет. */
export function shouldReport(e: unknown): boolean {
  return !(e instanceof ApiError) || e.code === 'NETWORK' || e.status >= 500;
}
